import Button from '@mui/material/Button/Button';
import React from 'react';
import {
    HomeIcon,
    HowToIcon,
    ArticleIcon,
    ResultsIcon,
    CrewIcon,
    MentorIcon,
    ProfileIcon,
    EditIcon,
    SettingsIcon,
    AdminIcon,
    MentorshipIcon,
    MentorshipPlanningIcon,
    BadgeIcon,
    TeamsIcon,
    MembersIcon,
    FormTemplateIcon,
    NewFormIcon,
    CompetencyIcon,
    PricingIcon,
    BlogIcon,
    GoalsIcon,
    CrewSetupIcon,
    PulseCheckIcon,
    PrivacyIcon,
    RetroIcon,
    ApiKeyIcon,
    ApiDocsIcon,
    FeedIcon,
    FeedPostIcon,
    ProjectIcon,
} from '../../constants/icons';

import Typography from '@mui/material/Typography/Typography';
import { Link } from 'react-router-dom';
import { QRCodeDialog } from '../QRContainer';
import Breadcrumbs from '@mui/material/Breadcrumbs/Breadcrumbs';
import Stack from '@mui/material/Stack/Stack';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useDesktopMode } from '../../services/hooks/useDesktopMode';
import { useState } from 'react';

const buttonStyle = {
    alignItems: 'center',
    display: 'flex',
    color: 'inherit'
};

export const HomeButton = () => {
    return (
        <Button component={Link} to="/" sx={buttonStyle}>
            <HomeIcon />
            <Typography>Home</Typography>
        </Button>
    );
};

export enum PageType {
    Home,
    HowTo,
    Article,
    Results,
    Crew,
    Mentor,
    Competencies,
    Profile,
    Pricing,
    Edit,
    Forms,
    NewForm,
    Settings,
    Admin,
    Mentorship,
    MentorshipPlanning,
    Members,
    Badges,
    Teams,
    Team,
    Blog,
    BlogPost,
    Goal,
    CrewSetup,
    Pulse,
    Privacy,
    Retro,
    ApiKeys,
    ApiDocs,
    Feed,
    FeedPost,
    Project
}

const iconMap: { [key in PageType]: JSX.Element } = {
    [PageType.Home]: <HomeIcon />,
    [PageType.HowTo]: <HowToIcon />,
    [PageType.Article]: <ArticleIcon />,
    [PageType.Results]: <ResultsIcon />,
    [PageType.Crew]: <CrewIcon />,
    [PageType.Mentor]: <MentorIcon />,
    [PageType.Profile]: <ProfileIcon />,
    [PageType.Edit]: <EditIcon />,
    [PageType.Settings]: <SettingsIcon />,
    [PageType.Admin]: <AdminIcon />,
    [PageType.Mentorship]: <MentorshipIcon />,
    [PageType.Badges]: <BadgeIcon />,
    [PageType.Teams]: <TeamsIcon />,
    [PageType.Team]: <TeamsIcon />,
    [PageType.Forms]: <FormTemplateIcon />,
    [PageType.NewForm]: <NewFormIcon />,
    [PageType.Competencies]: <CompetencyIcon />,
    [PageType.Members]: <MembersIcon />,
    [PageType.MentorshipPlanning]: <MentorshipPlanningIcon />,
    [PageType.Pricing]: <PricingIcon />,
    [PageType.Blog]: <BlogIcon />,
    [PageType.BlogPost]: <ArticleIcon />,
    [PageType.Goal]: <GoalsIcon />,
    [PageType.CrewSetup]: <CrewSetupIcon />,
    [PageType.Pulse]: <PulseCheckIcon />,
    [PageType.Privacy]: <PrivacyIcon />,
    [PageType.Retro]: <RetroIcon />,
    [PageType.ApiKeys]: <ApiKeyIcon />,
    [PageType.ApiDocs]: <ApiDocsIcon />,
    [PageType.Feed]: <FeedIcon />,
    [PageType.FeedPost]: <FeedPostIcon />,
    [PageType.Project]: <ProjectIcon />,

};

export const AppBreadcrumbs = ({
    crumbs
}: {
    crumbs: Array<{
        type: PageType,
        name: string,
        link?: string,
    }>
}) => {
    const desktopMode = useDesktopMode();

    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

    if ((!desktopMode || crumbs.length > 4) && crumbs.length > 1) {
        const parent = crumbs[crumbs.length - 2];
        const current = crumbs[crumbs.length - 1];
        const ancestors = crumbs.slice(0, -1).reverse();
        return (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ py: 1 }}>
                {parent.link ? (
                    <IconButton component={Link} to={parent.link} size="small" aria-label={`Back to ${parent.name}`}>
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                ) : (
                    <IconButton size="small" disabled aria-label={parent.name}>
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                )}
                <Button
                    size="small"
                    endIcon={<ExpandMoreIcon fontSize="small" />}
                    onClick={(e) => setMenuAnchor(e.currentTarget)}
                    sx={{ textTransform: 'none', fontWeight: 500, minWidth: 0, px: 0.5 }}
                >
                    <Typography variant="body2" noWrap sx={{ fontWeight: 500, maxWidth: 180 }}>
                        {current.name}
                    </Typography>
                </Button>
                <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                    {ancestors.map((crumb, index) => (
                        <MenuItem
                            key={index}
                            component={crumb.link ? Link : 'li'}
                            to={crumb.link ?? ''}
                            onClick={() => setMenuAnchor(null)}
                            disabled={!crumb.link}
                        >
                            <ListItemIcon sx={{ minWidth: 32 }}>{iconMap[crumb.type]}</ListItemIcon>
                            <ListItemText primary={crumb.name} />
                        </MenuItem>
                    ))}
                </Menu>
                <QRCodeDialog />
            </Stack>
        );
    }

    const parent = crumbs.length > 1 ? crumbs[crumbs.length - 2] : null;
    return (
        <Stack direction="row" alignItems="center" spacing={0.5}>
            {parent?.link ? (
                <IconButton component={Link} to={parent.link} size="small" aria-label={`Back to ${parent.name}`}>
                    <ArrowBackIcon fontSize="small" />
                </IconButton>
            ) : parent ? (
                <IconButton size="small" disabled aria-label={parent.name}>
                    <ArrowBackIcon fontSize="small" />
                </IconButton>
            ) : null}
            <Breadcrumbs sx={{ py: 1 }}>
            {
                crumbs.map((crumb, index) => {
                    if (crumb.link) {
                        return (
                            <Button key={index} component={Link} to={crumb.link} sx={buttonStyle}>
                                {iconMap[crumb.type]}
                                <Typography>{crumb.name}</Typography>
                                {
                                    index === crumbs.length - 1 && <QRCodeDialog />
                                }
                            </Button>
                        );
                    }

                    return (
                        <Stack key={index} direction="row" alignItems="center" spacing={0.5} sx={buttonStyle}>
                            {iconMap[crumb.type]}
                            <Typography>{crumb.name}</Typography>
                            {
                                index === crumbs.length - 1 && <QRCodeDialog />
                            }
                        </Stack>
                    );
                })
            }
        </Breadcrumbs>
        </Stack>
    );

};
